import { z } from 'zod';
import { ecosystemSchema } from './ecosystem';

/** Coarse severity bucket derived from CVSS score (or OSV severity data). */
export const severityLevelSchema = z.enum(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'UNKNOWN']);
export type SeverityLevel = z.infer<typeof severityLevelSchema>;

/** Buckets a CVSS v3 base score per the standard ranges. */
export function severityFromScore(score: number | null | undefined): SeverityLevel {
  if (score == null) return 'UNKNOWN';
  if (score >= 9) return 'CRITICAL';
  if (score >= 7) return 'HIGH';
  if (score >= 4) return 'MEDIUM';
  if (score > 0) return 'LOW';
  return 'UNKNOWN';
}

/**
 * One affected branch from OSV `affected[].ranges[]`, flattened from its
 * introduced/fixed/last_affected events. `fixed: null` with `lastAffected`
 * present means no patch exists on that branch. GIT ranges are excluded.
 */
export const fixedInRangeSchema = z.object({
  type: z.enum(['SEMVER', 'ECOSYSTEM']),
  introduced: z.string().nullable(),
  fixed: z.string().nullable(),
  lastAffected: z.string().nullable(),
});
export type FixedInRange = z.infer<typeof fixedInRangeSchema>;

/**
 * An advisory as it applies to one specific package version (Flow 1). The
 * ranges/fixed versions are already filtered to the package in question.
 */
export const versionAdvisorySchema = z.object({
  id: z.string().min(1),
  aliases: z.array(z.string()),
  summary: z.string().nullable(),
  severity: severityLevelSchema,
  cvssScore: z.number().nullable(),
  cvssVector: z.string().nullable(),
  /** All maintained-branch ranges for this package (may be several). */
  ranges: z.array(fixedInRangeSchema),
  /** Distinct `fixed` versions across ranges — the "fixed in" badges. */
  fixedVersions: z.array(z.string()),
  /** OSV record URL for the full details. */
  url: z.string().nullable(),
});
export type VersionAdvisory = z.infer<typeof versionAdvisorySchema>;

/** A package affected by an advisory (Flow 2), with its fix ranges. */
export const affectedPackageSchema = z.object({
  /** Raw OSV ecosystem string (may be outside our supported set, e.g. Debian). */
  osvEcosystem: z.string(),
  /** Our ecosystem enum when the OSV ecosystem maps to one, else null. */
  system: ecosystemSchema.nullable(),
  name: z.string().min(1),
  ranges: z.array(fixedInRangeSchema),
  fixedVersions: z.array(z.string()),
});
export type AffectedPackage = z.infer<typeof affectedPackageSchema>;

export const advisoryReferenceSchema = z.object({
  type: z.string(),
  url: z.string(),
  /** NVD reference tags (e.g. "Patch", "Vendor Advisory"); OSV refs get []. */
  tags: z.array(z.string()).default([]),
});
export type AdvisoryReference = z.infer<typeof advisoryReferenceSchema>;

/**
 * A CWE weakness from NVD (`weaknesses[].description[]`). NVD rarely supplies a
 * human name, so `name` is usually null and the UI links to cwe.mitre.org.
 */
export const cweSchema = z.object({
  /** Canonical CWE id, e.g. `CWE-79`. */
  id: z.string(),
  name: z.string().nullable(),
});
export type Cwe = z.infer<typeof cweSchema>;

/**
 * One CPE match criterion from an NVD configuration node
 * (`configurations[].nodes[].cpeMatch[]`). Describes an affected-product range
 * in CPE terms; kept separate from OSV package `affected[]`.
 */
export const cpeMatchSchema = z.object({
  /** CPE 2.3 URI, e.g. `cpe:2.3:a:apache:log4j:*:*:*:*:*:*:*:*`. */
  criteria: z.string(),
  vulnerable: z.boolean(),
  versionStartIncluding: z.string().nullable(),
  versionStartExcluding: z.string().nullable(),
  versionEndIncluding: z.string().nullable(),
  versionEndExcluding: z.string().nullable(),
});
export type CpeMatch = z.infer<typeof cpeMatchSchema>;

/**
 * Response for `GET /api/v1/advisories/:id` (accepts CVE or GHSA ids).
 *
 * NVD-sourced fields (`weaknesses`, `cpeMatches`, `vulnStatus`, `nvdSeverity`,
 * `hasNvdData`) are all defaulted so payloads cached before NVD enrichment
 * landed — and old frontends re-parsing — still validate.
 */
export const advisoryDetailSchema = z.object({
  id: z.string().min(1),
  aliases: z.array(z.string()),
  summary: z.string().nullable(),
  details: z.string().nullable(),
  severity: severityLevelSchema,
  cvssScore: z.number().nullable(),
  cvssVector: z.string().nullable(),
  published: z.string().nullable(),
  modified: z.string().nullable(),
  references: z.array(advisoryReferenceSchema),
  affected: z.array(affectedPackageSchema),
  /** CWE weaknesses from NVD (empty when NVD has no data). */
  weaknesses: z.array(cweSchema).default([]),
  /** CPE affected-product match criteria from NVD (capped at 100 server-side). */
  cpeMatches: z.array(cpeMatchSchema).default([]),
  /** NVD `vulnStatus` (e.g. "Analyzed", "Modified"); null when absent. */
  vulnStatus: z.string().nullable().default(null),
  /** NVD's own base severity, mapped to our enum; lets the UI show it when no CVSS exists. */
  nvdSeverity: severityLevelSchema.nullable().default(null),
  /** True when NVD returned a record for this CVE. */
  hasNvdData: z.boolean().default(false),
});
export type AdvisoryDetail = z.infer<typeof advisoryDetailSchema>;

/** Matches ids the advisory lookup accepts (and the home-search autodetect). */
export const advisoryIdPattern = /^(CVE-\d{4}-\d{4,}|GHSA(-[23456789cfghjmpqrvwx]{4}){3})$/i;
export const advisoryIdSchema = z
  .string()
  .trim()
  .regex(advisoryIdPattern, 'Expected a CVE-YYYY-NNNN or GHSA-xxxx-xxxx-xxxx id');

/**
 * Normalizes an advisory id to its canonical case so variant spellings hit
 * the same cache entry: CVE ids are upper-case; GHSA suffixes stay lower-case
 * because OSV record lookups are case-sensitive.
 */
export function canonicalAdvisoryId(id: string): string {
  return id.toUpperCase().startsWith('GHSA-')
    ? `GHSA-${id.slice(5).toLowerCase()}`
    : id.toUpperCase();
}
